
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ArrowUpDown, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

const ItinerariesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'published', 'draft'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'title'

  // Fetch itineraries from Supabase
  const { data: itineraries = [], isLoading, error } = useQuery({
    queryKey: ['itineraries'],
    queryFn: async () => {
      try {
        // First, fetch the itineraries
        const { data: itinerariesData, error: itinerariesError } = await supabase
          .from('itineraries')
          .select('*, itinerary_days (*)');
        
        if (itinerariesError) {
          console.error('Error fetching itineraries:', itinerariesError);
          throw itinerariesError;
        }
        
        if (!itinerariesData || itinerariesData.length === 0) {
          return [];
        }

        // Then fetch author info separately for each itinerary
        const itinerariesWithDetails = await Promise.all(
          itinerariesData.map(async (itinerary) => {
            let authorData = { first_name: '', last_name: '' };
            
            if (itinerary.author_id) {
              const { data: author, error: authorError } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', itinerary.author_id)
                .maybeSingle();
              
              if (!authorError && author) {
                authorData = author;
              }
            }
            
            return {
              ...itinerary,
              author: authorData,
              steps: itinerary.itinerary_days ? itinerary.itinerary_days.length : 0,
              tags: [itinerary.destination].filter(Boolean)
            };
          })
        );

        return itinerariesWithDetails;
      } catch (error) {
        console.error('Error in fetching itineraries:', error);
        toast.error('Failed to load itineraries');
        return [];
      }
    }
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Filter and sort the itineraries
  const filteredItineraries = itineraries
    .filter((itinerary) => {
      // Filter by status
      if (filter !== 'all' && itinerary.status !== filter) return false;
      
      // Filter by search term
      if (
        searchTerm &&
        !itinerary.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(itinerary.author?.first_name + ' ' + itinerary.author?.last_name).toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(itinerary.destination || '').toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by selected criteria
      if (sortBy === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Itineraries</h1>
          <p className="text-muted-foreground">Plan and manage trip itineraries</p>
        </div>
        <Button onClick={() => navigate('/dashboard/itineraries/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Itinerary
        </Button>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle>All Itineraries</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                type="search" 
                placeholder="Search itineraries..." 
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setFilter('all')}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('published')}>
                  Published
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('draft')}>
                  Draft
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Sort
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Sort By</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setSortBy('newest')}>
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('title')}>
                  Title (A-Z)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">Error loading itineraries. Please try again.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItineraries.length > 0 ? (
                filteredItineraries.map((itinerary) => (
                  <Card 
                    key={itinerary.id} 
                    className="glass-card hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/dashboard/itineraries/${itinerary.id}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="rounded-full p-2 bg-primary/10">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                        <Badge
                          variant={itinerary.status === 'published' ? 'default' : 'outline'}
                          className="capitalize"
                        >
                          {itinerary.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-lg mb-1">{itinerary.title}</h3>
                      <div className="text-sm text-muted-foreground mb-4">
                        <div className="flex items-center space-x-1">
                          <span>{itinerary.destination || 'No location'}</span>
                          <span>•</span>
                          <span>{itinerary.steps} steps</span>
                        </div>
                        <div>
                          By {itinerary.author 
                            ? `${itinerary.author.first_name || ''} ${itinerary.author.last_name || ''}` 
                            : 'Unknown'} on {formatDate(itinerary.created_at)}
                        </div>
                      </div>
                      {itinerary.tags && itinerary.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-auto">
                          {itinerary.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full p-8 text-center">
                  <p className="text-muted-foreground">No itineraries found. Try adjusting your filters or create a new itinerary.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ItinerariesPage;
